import * as React from 'react'
import { ReactElement, useContext, useMemo, useRef, useState } from 'react'

import OverflowToolbarPanel, { HammockToolbarButtonData, HammockToolbarProps } from '../../../../components/Overflow/OverflowToolbarPanel'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { FileRouteMatchProps, usePageStyles, ViewFileProps } from '../ViewFile'
import DocumentFlatTree, { DocumentSelectionStrategy, FolderDriveItem } from '../../../../components/Document/DocumentFlatTree'
import { Button, Card, MessageBar, MessageBarActions, MessageBarBody } from '@fluentui/react-components'
import {
    AddSquareMultipleFilled,
    AddSquareMultipleRegular,
    ArrowSortFilled,
    ArrowSortRegular,
    ArrowUploadFilled,
    ArrowUploadRegular,
    bundleIcon,
    ClipboardTaskAddFilled,
    ClipboardTaskAddRegular,
    CopyArrowRightFilled,
    CopyArrowRightRegular,
    CopySelectFilled,
    CopySelectRegular,
    DeleteFilled,
    DeleteRegular,
    DismissRegular,
    DocumentAddFilled,
    DocumentAddRegular,
    DocumentPdfFilled,
    DocumentPdfRegular,
    FolderAddFilled,
    FolderAddRegular,
    FolderZipFilled,
    FolderZipRegular,
    MailAddFilled,
    MailAddRegular,
    MailAttachFilled,
    MailAttachRegular,
    RenameFilled,
    RenameRegular,
} from '@fluentui/react-icons'
import SimpleDocumentFlatTreeItem from '../../../../components/Document/SimpleDocumentFlatTreeItem'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import HammockDialog, { useDialogStyles } from '../../../components/Dialog/HammockDialog'
import { useTranslation } from 'react-i18next'
import CreateFolderDialogBody, {
    CreateFolderDialogBodyProps,
    CreateFolderDialogBodyValues,
    getCreateRootFolderDialogBodySchema,
    getCreateSubFolderDialogBodySchema,
} from '../../../components/Dialog/DriveItem/CreateFolderDialogBody'
import HammockFormDialog from '../../../components/Dialog/HammockFormDialog'
import OfficeDocumentDialogBody, {
    getOfficeDocumentDialogBodySchema,
    OfficeDocumentDialogBodyProps,
    OfficeDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/OfficeDocumentDialogBody'
import { DocumentContext, DocumentSortOrder } from '../../../../config/documentConfig'
import PreviewDocumentDialogBody, { PreviewDocumentDialogBodyProps } from '../../../components/Dialog/DriveItem/PreviewDocumentDialogBody'
import MutateDocumentDialogBody, {
    getMutateDocumentDialogBodySchema,
    MutateDocumentAction,
    MutateDocumentDialogBodyProps,
    MutateDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/MutateDocumentDialogBody'
import DeleteDocumentDialogBody, { DeleteDocumentDialogBodyProps } from '../../../components/Dialog/DriveItem/DeleteDocumentDialogBody'
import { getDownloadUrl, getDriveItemDownloadUrl, isConvertableToPdf } from '../../../../helpers/documentHelper'
import { buildZipParams, validateZipSelection } from '../../../../helpers/zipHelper'
import MessageContext, { MAX_MESSAGE_SIZE } from '../../../../context/MessageContext'
import useSubmitDocumentGenerationRequest from '../../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import {
    DocumentGenerationContextData,
    DocumentGenerationStatusData,
    DocumentGenerationType,
} from '../../../../hooks/document/useDocumentGenerationHandler'
import { QueueData, QueueStatus } from '../../../../hooks/useQueueStateHandler'
import { addFileAttachmentAsync, isOfficeInitialized } from '../../../../helpers/outlookHelper'
import { generatePath } from 'react-router'
import { fileRoutePrefix } from '../../../../hooks/file/useFilePages'
import { ModuleType } from '../../../../context/GlobalContext'
import useModules from '../../../../hooks/module/useModules'
import { useHistory, useRouteMatch } from 'react-router-dom'
import ComposeEmailDialogBody, { ComposeEmailDialogBodyProps } from '../../../components/Dialog/OutlookItem/ComposeEmailDialogBody'
import { isAppContext } from '../../../../helpers/appHelper'
import { AppContext } from '../../../../config/appConfig'
import useAxios from '../../../../hooks/useAxios'
import Dropzone from '../../../../components/Dropzone/Dropzone'
import ImportDocumentDialogBody, {
    getImportDocumentDialogBodySchema,
    ImportDocumentDialogBodyProps,
    ImportDocumentDialogBodyValues,
} from '../../../components/Dialog/DriveItem/ImportDocumentDialogBody'
import { DropzoneRef } from 'react-dropzone'
import useLocalStorage from '../../../../hooks/useLocalStorage'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import Loading from '../../../../components/Loading'
import { useApplicationStyles } from '../../../../pages/App'

const AddIcon = bundleIcon(AddSquareMultipleFilled, AddSquareMultipleRegular)
const AttachIcon = bundleIcon(MailAttachFilled, MailAttachRegular)
const DocumentAddIcon = bundleIcon(DocumentAddFilled, DocumentAddRegular)
const TaskAddIcon = bundleIcon(ClipboardTaskAddFilled, ClipboardTaskAddRegular)
const MailAddIcon = bundleIcon(MailAddFilled, MailAddRegular)
const DocumentPdfIcon = bundleIcon(DocumentPdfFilled, DocumentPdfRegular)
const FolderAddIcon = bundleIcon(FolderAddFilled, FolderAddRegular)
const FolderZipIcon = bundleIcon(FolderZipFilled, FolderZipRegular)
const RenameIcon = bundleIcon(RenameFilled, RenameRegular)
const CopyIcon = bundleIcon(CopySelectFilled, CopySelectRegular)
const MoveIcon = bundleIcon(CopyArrowRightFilled, CopyArrowRightRegular)
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular)
const ImportIcon = bundleIcon(ArrowUploadFilled, ArrowUploadRegular)
const SortIcon = bundleIcon(ArrowSortFilled, ArrowSortRegular)

type FileDocumentOverviewProps = ViewFileProps & {
    selectedDriveItems: FolderDriveItem[]
    setSelectedDriveItems: (items: FolderDriveItem[]) => void
}

export default function FileDocumentOverview(props: FileDocumentOverviewProps): ReactElement {
    const appStyles = useApplicationStyles()
    const axios = useAxios()
    const history = useHistory()
    const { host } = useContext(AuthorizationContext)
    const { maxMessageSize } = useContext(MessageContext)
    const modules = useModules()
    const pageStyles = usePageStyles()
    const dialogStyles = useDialogStyles()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])
    const routeMatch = useRouteMatch<FileRouteMatchProps>()

    const toolbarProps = useMemo<HammockToolbarProps>(
        (): HammockToolbarProps => ({
            size: 'small',
            className: pageStyles.toolbar,
        }),
        [smallMenu],
    )

    const {
        loaded: loadedSortOrder,
        values: sortOrder,
        setValues: setSortOrder,
    } = useLocalStorage<DocumentSortOrder>({
        key: 'HammockFileDocumentOverviewSorting',
        hostKey: host?.RowKey,
        defaultValue: DocumentSortOrder.ModifiedDesc,
        fromJson: values => values,
        toJson: values => values,
        load: host !== undefined,
    })

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [openCreateStandardDocument, setOpenCreateStandardDocument] = useState(false)
    const [openCreateRootFolder, setOpenCreateRootFolder] = useState(false)
    const [openCreateSubFolder, setOpenCreateSubFolder] = useState(false)
    const [openMutateDocuments, setOpenMutateDocuments] = useState<Pick<MutateDocumentDialogBodyProps, 'action' | 'items'>>()
    const [openImportDocuments, setOpenImportDocuments] = useState<Pick<ImportDocumentDialogBodyProps, 'items'>>()
    const [openDeleteDocuments, setOpenDeleteDocuments] = useState(false)
    const [openComposeEmail, setOpenComposeEmail] = useState(false)
    const [openPreviewDocument, setOpenPreviewDocument] = useState<FolderDriveItem>()

    const dropzoneRef = useRef<DropzoneRef>()

    const [allDriveItems, setAllDriveItems] = useState<FolderDriveItem[]>(undefined)
    const [filteredDriveItems, setFilteredDriveItems] = useState<FolderDriveItem[]>(undefined)
    const documentDriveItems = useMemo(
        () => (filteredDriveItems ? filteredDriveItems : allDriveItems)?.filter(item => item.itemType === 'leaf'),
        [allDriveItems, filteredDriveItems],
    )

    const updateDriveItemsFunc = useRef<(addDriveItems: DriveItem[], removeDriveItems: DriveItem[]) => void>(() => {})
    const reloadDriveItemsFunc = useRef<() => void>(() => {})

    const [errors, setErrors] = useState<Error[]>([])

    const submitPdfRequest = useSubmitDocumentGenerationRequest(
        DocumentGenerationType.PdfConversion,
        {
            [QueueStatus.Finished]: () => {
                reloadDriveItemsFunc.current?.()
            },
        },
        true,
    )

    const submitZipRequest = useSubmitDocumentGenerationRequest(
        DocumentGenerationType.Zip,
        {
            [QueueStatus.Finished]: () => {
                reloadDriveItemsFunc.current?.()
            },
        },
        true,
    )

    const buttons = useMemo<HammockToolbarButtonData[]>((): HammockToolbarButtonData[] => {
        return [
            {
                value: 'email-add-document',
                icon: <AttachIcon />,
                titleKey: 'file.pivot.document.email.add',
                filter: () => isOfficeInitialized() && isAppContext([AppContext.Compose]),
                disabled: props.selectedDriveItems.filter(item => item.itemType === 'leaf').length === 0,
                onClick: () => {
                    Promise.all(
                        props.selectedDriveItems
                            .filter(item => item.itemType === 'leaf')
                            .map(item =>
                                getDriveItemDownloadUrl(item.id, axios).then(downloadUrl => {
                                    addFileAttachmentAsync(downloadUrl, item.name, undefined, (_, error) => {
                                        if (error) {
                                            setErrors([...errors, new Error(`${item.name}: ${error.message}`)])
                                        }
                                    })
                                }),
                            ),
                    ).then(() => {
                        props.setSelectedDriveItems([])
                        props.setSelectedDriveItemIds([])
                    })
                },
            },
            {
                value: 'email-add-document-pdf',
                icon: <DocumentPdfIcon />,
                titleKey: 'file.pivot.document.email.add.pdf',
                filter: () => isOfficeInitialized() && isAppContext([AppContext.Compose]),
                disabled: props.selectedDriveItems.filter(item => item.itemType === 'leaf').length === 0,
                onClick: () => {
                    props.selectedDriveItems
                        .filter(item => item.itemType === 'leaf')
                        .forEach(async item => {
                            if (isConvertableToPdf(item.name)) {
                                const payload = {
                                    itemId: item.id,
                                    fileName: item.name,
                                    folderId: item.parent.id,
                                }
                                submitPdfRequest(payload, item.name.replace(/\.[a-zA-Z]{3,}$/, '.pdf'), {
                                    [QueueStatus.Finished]: (e: QueueData<any, DocumentGenerationStatusData, DocumentGenerationContextData>) => {
                                        setTimeout(() => {
                                            addFileAttachmentAsync(
                                                getDownloadUrl(e.status.driveItem),
                                                e.status.driveItem.name,
                                                undefined,
                                                (_, error) => {
                                                    if (error) {
                                                        setErrors([...errors, new Error(`${item.name}: ${error.message}`)])
                                                    }
                                                },
                                            )
                                        }, Math.random() * 2000)
                                    },
                                })
                            } else {
                                getDriveItemDownloadUrl(item.id, axios).then(downloadUrl => {
                                    setTimeout(
                                        () =>
                                            addFileAttachmentAsync(downloadUrl, item.name, undefined, (_, error) => {
                                                if (error) {
                                                    setErrors([...errors, new Error(`${item.name}: ${error.message}`)])
                                                }
                                            }),
                                        Math.random() * 2000,
                                    )
                                })
                            }
                        })
                    props.setSelectedDriveItems([])
                    props.setSelectedDriveItemIds([])
                },
            },
            {
                value: 'email-add-document-zip',
                icon: <FolderZipIcon />,
                titleKey: 'file.pivot.document.email.add.zip',
                filter: () => isOfficeInitialized() && isAppContext([AppContext.Compose]),
                disabled: props.selectedDriveItems.length === 0,
                onClick: () => {
                    const selectedDriveItems = props.selectedDriveItems
                    const validationError = validateZipSelection(selectedDriveItems, translate, maxMessageSize?.data ?? MAX_MESSAGE_SIZE)
                    if (validationError) {
                        setErrors([...errors, validationError])
                        return
                    }
                    const payload = buildZipParams(selectedDriveItems, props.file.documentRootId, props.file.reference)
                    submitZipRequest(payload, payload.fileName, {
                        [QueueStatus.Finished]: (e: QueueData<any, DocumentGenerationStatusData, DocumentGenerationContextData>) => {
                            addFileAttachmentAsync(getDownloadUrl(e.status.driveItem), e.status.driveItem.name, undefined, (_, error) => {
                                if (error) {
                                    setErrors([...errors, new Error(`${e.status.driveItem.name}: ${error.message}`)])
                                }
                            })
                        },
                    })
                    props.setSelectedDriveItems([])
                    props.setSelectedDriveItemIds([])
                },
            },
            {
                value: 'add',
                icon: <AddIcon />,
                titleKey: 'file.pivot.document.create.label',
                filter: () => !isAppContext([AppContext.Compose]),
                childButtons: [
                    {
                        value: 'add-document',
                        icon: <DocumentAddIcon />,
                        titleKey: 'file.pivot.document.create.standard',
                        onClick: () => setOpenCreateStandardDocument(true),
                    },
                    {
                        value: 'add-task',
                        icon: <TaskAddIcon />,
                        titleKey: 'file.pivot.document.create.task',
                        onClick: () =>
                            history.push(
                                generatePath(`${fileRoutePrefix}/${modules.has(ModuleType.Workflow) ? 'workflow' : 'task'}/add`, routeMatch.params),
                                {
                                    attachmentIds: props.selectedDriveItems?.map(item => item.id) ?? [],
                                },
                            ),
                        disabled: props.selectedDriveItems.length === 0,
                    },
                    {
                        value: 'add-email',
                        icon: <MailAddIcon />,
                        titleKey: 'file.pivot.document.create.email',
                        onClick: () => setOpenComposeEmail(true),
                        filter: () => isOfficeInitialized(),
                        disabled: props.selectedDriveItems.length === 0,
                    },
                    {
                        value: 'add-root-folder',
                        icon: <FolderAddIcon />,
                        titleKey: 'file.pivot.document.create.folder.root',
                        onClick: () => setOpenCreateRootFolder(true),
                    },
                    {
                        value: 'add-sub-folder',
                        icon: <FolderAddIcon />,
                        titleKey: 'file.pivot.document.create.folder.sub',
                        onClick: () => setOpenCreateSubFolder(true),
                    },
                ],
            },
            {
                value: 'divider1',
                divider: true,
                filter: () => !isAppContext([AppContext.Compose]),
            },
            {
                value: 'convert-pdf',
                icon: <DocumentPdfIcon />,
                titleKey: 'file.pivot.document.convert.pdf',
                filter: () => !isAppContext([AppContext.Compose]),
                disabled:
                    props.selectedDriveItems.length === 0 ||
                    props.selectedDriveItems.filter(item => item.itemType === 'branch').length > 0 ||
                    props.selectedDriveItems.some(item => !isConvertableToPdf(item.name)),
                onClick: () => {
                    props.selectedDriveItems.forEach(item => {
                        if (isConvertableToPdf(item.name)) {
                            const payload = {
                                itemId: item.id,
                                fileName: item.name,
                                folderId: item.parent.id,
                            }
                            submitPdfRequest(payload, item.name.replace(/\.[a-zA-Z]{3,}$/, '.pdf'))
                        }
                    })
                },
            },
            {
                value: 'rename',
                icon: <RenameIcon />,
                titleKey: 'file.pivot.document.mutate.rename.button',
                disabled: props.selectedDriveItems.length !== 1,
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenMutateDocuments({ action: MutateDocumentAction.Rename, items: props.selectedDriveItems }),
            },
            {
                value: 'copy',
                icon: <CopyIcon />,
                titleKey: 'file.pivot.document.mutate.copy.button',
                disabled: props.selectedDriveItems.length === 0,
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenMutateDocuments({ action: MutateDocumentAction.Copy, items: props.selectedDriveItems }),
            },
            {
                value: 'move',
                icon: <MoveIcon />,
                titleKey: 'file.pivot.document.mutate.move.button',
                disabled: props.selectedDriveItems.length === 0,
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenMutateDocuments({ action: MutateDocumentAction.Move, items: props.selectedDriveItems }),
            },
            {
                value: 'delete',
                icon: <DeleteIcon />,
                titleKey: 'file.pivot.document.delete.button',
                disabled: props.selectedDriveItems.length === 0,
                filter: () => !isAppContext([AppContext.Compose]),
                onClick: () => setOpenDeleteDocuments(true),
            },
            {
                value: 'import',
                icon: <ImportIcon />,
                titleKey: 'file.pivot.document.import.button',
                onClick: () => dropzoneRef.current?.open(),
            },
            {
                value: 'sort',
                icon: <SortIcon />,
                titleKey: 'sorting.label',
                childButtons: [
                    {
                        value: 'filter-a-z',
                        titleKey: 'sorting.az',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.NameAsc),
                    },
                    {
                        value: 'filter-z-a',
                        titleKey: 'sorting.za',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.NameDesc),
                    },
                    {
                        value: 'filter-old-new',
                        titleKey: 'sorting.dateasc',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.ModifiedAsc),
                    },
                    {
                        value: 'filter-new-old',
                        titleKey: 'sorting.datedesc',
                        icon: <SortIcon />,
                        onClick: () => setSortOrder(DocumentSortOrder.ModifiedDesc),
                    },
                ],
            },
        ]
    }, [props.selectedDriveItems, setSortOrder])

    if (!loadedSortOrder) {
        return <Loading />
    }

    return (
        <>
            <Card className={appStyles.sticky}>
                <HammockFormDialog<Partial<OfficeDocumentDialogBodyValues>, OfficeDocumentDialogBodyProps>
                    open={openCreateStandardDocument}
                    setOpen={setOpenCreateStandardDocument}
                    dialogFormProps={() => ({
                        initialValues: {
                            fileId: props.fileId,
                            folderId: props.selectedDriveItems.filter(item => item.itemType === 'branch')?.[0]?.id,
                        },
                        onSubmit: () => {},
                        validationSchema: getOfficeDocumentDialogBodySchema(translate),
                    })}
                    dialogFormBody={OfficeDocumentDialogBody}
                    dialogFormBodyProps={() => ({
                        context: DocumentContext.Document,
                        file: props.file,
                        documentRootId: props.file.documentRootId,
                        folders: allDriveItems,
                        parentId: props.selectedDriveItems.filter(item => item.itemType === 'branch')?.[0]?.id,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                    })}
                />
                <HammockFormDialog<CreateFolderDialogBodyValues, CreateFolderDialogBodyProps>
                    open={openCreateRootFolder}
                    setOpen={setOpenCreateRootFolder}
                    dialogFormProps={() => ({
                        initialValues: {
                            folderId: undefined,
                            name: undefined,
                        },
                        onSubmit: () => {},
                        validationSchema: getCreateRootFolderDialogBodySchema(translate),
                    })}
                    dialogFormBody={CreateFolderDialogBody}
                    dialogFormBodyProps={() => ({
                        isRoot: true,
                        folders: allDriveItems,
                        parentId: props.file.documentRootId,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                        },
                    })}
                />
                <HammockFormDialog<CreateFolderDialogBodyValues, CreateFolderDialogBodyProps>
                    open={openCreateSubFolder}
                    setOpen={setOpenCreateSubFolder}
                    dialogFormProps={() => ({
                        initialValues: {
                            folderId: props.selectedDriveItems.filter(item => item.itemType === 'branch')?.[0]?.id,
                            name: undefined,
                        },
                        onSubmit: () => {},
                        validationSchema: getCreateSubFolderDialogBodySchema(translate),
                    })}
                    dialogFormBody={CreateFolderDialogBody}
                    dialogFormBodyProps={() => ({
                        isRoot: false,
                        folders: allDriveItems,
                        parentId: undefined,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                    })}
                />
                <HammockFormDialog<Partial<MutateDocumentDialogBodyValues>, MutateDocumentDialogBodyProps>
                    open={openMutateDocuments?.items?.length > 0}
                    setOpen={(open: boolean) =>
                        setOpenMutateDocuments(open ? openMutateDocuments : { action: MutateDocumentAction.Rename, items: [] })
                    }
                    dialogFormProps={() => ({
                        initialValues: {
                            name: openMutateDocuments?.items?.[0]?.name,
                            fileId: props.fileId,
                            folderId: openMutateDocuments?.items?.[0]?.parent?.id,
                        },
                        onSubmit: () => {},
                        validationSchema: getMutateDocumentDialogBodySchema(translate, openMutateDocuments?.items),
                    })}
                    dialogFormBody={MutateDocumentDialogBody}
                    dialogFormBodyProps={() => ({
                        file: props.file,
                        folders: allDriveItems,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                        ...openMutateDocuments,
                    })}
                />
                <HammockDialog<DeleteDocumentDialogBodyProps>
                    open={openDeleteDocuments}
                    setOpen={setOpenDeleteDocuments}
                    dialogBody={DeleteDocumentDialogBody}
                    dialogBodyProps={() => ({
                        items: props.selectedDriveItems,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                    })}
                />
                <HammockDialog<PreviewDocumentDialogBodyProps>
                    open={openPreviewDocument !== undefined}
                    setOpen={(open: boolean) => setOpenPreviewDocument(open ? openPreviewDocument : undefined)}
                    dialogBody={PreviewDocumentDialogBody}
                    surfaceProps={{
                        className: dialogStyles.dialogHeight,
                    }}
                    dialogBodyProps={() => ({
                        driveItem: openPreviewDocument,
                        callback: () => reloadDriveItemsFunc.current?.(),
                        hasPrevious: () => openPreviewDocument && documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id) > 0,
                        hasNext: () =>
                            openPreviewDocument &&
                            documentDriveItems?.length > documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id) + 1,
                        previous: () => {
                            const curIndex = documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id)
                            if (curIndex > 0) {
                                setOpenPreviewDocument(documentDriveItems?.[curIndex - 1])
                            }
                        },
                        next: () => {
                            const curIndex = documentDriveItems?.findIndex(value => value.id === openPreviewDocument.id)
                            if (documentDriveItems?.length > curIndex) {
                                setOpenPreviewDocument(documentDriveItems?.[curIndex + 1])
                            }
                        },
                    })}
                />
                <HammockDialog<ComposeEmailDialogBodyProps>
                    open={openComposeEmail}
                    setOpen={setOpenComposeEmail}
                    dialogBody={ComposeEmailDialogBody}
                    dialogBodyProps={() => props}
                />
                <HammockFormDialog<Partial<ImportDocumentDialogBodyValues>, ImportDocumentDialogBodyProps>
                    open={openImportDocuments?.items?.length > 0}
                    setOpen={(open: boolean) => setOpenImportDocuments(open ? openImportDocuments : { items: [] })}
                    dialogFormProps={() => ({
                        initialValues: {
                            folderId: props.selectedDriveItems[0]?.id,
                        },
                        onSubmit: () => {},
                        validationSchema: getImportDocumentDialogBodySchema(translate),
                    })}
                    dialogFormBody={ImportDocumentDialogBody}
                    dialogFormBodyProps={() => ({
                        file: props.file,
                        folders: allDriveItems,
                        callback: () => {
                            reloadDriveItemsFunc.current?.()
                            props.setSelectedDriveItems([])
                            props.setSelectedDriveItemIds([])
                        },
                        ...openImportDocuments,
                    })}
                />

                <OverflowToolbarPanel
                    toolbarProps={toolbarProps}
                    getToolbarButtonProps={(button?: HammockToolbarButtonData) => ({
                        appearance: 'subtle',
                        disabled: isLoading || button?.disabled,
                    })}
                    getMenuItemProps={(button?: HammockToolbarButtonData) => ({
                        disabled: isLoading || button?.disabled,
                    })}
                    buttons={buttons}
                    filter={buttons => buttons?.filter(button => (button.filter ? button.filter(button) : true))}
                    overflowButtonLabelKey={'base.item.label'}
                />
                {errors?.map((error, index) => (
                    <MessageBar intent={'error'} key={index}>
                        <MessageBarBody>{error.message}</MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    appearance="transparent"
                                    aria-label="Dismiss"
                                    icon={<DismissRegular />}
                                    onClick={() => setErrors([...errors.filter(item => item.message !== error.message)])}
                                />
                            }
                        ></MessageBarActions>
                    </MessageBar>
                ))}
            </Card>
            <Card>
                <Dropzone
                    disabled={allDriveItems?.filter(item => item.itemType === 'branch').length === 0}
                    dropzoneRef={dropzoneRef}
                    onDropAccepted={(files: File[]) => {
                        setOpenImportDocuments({ items: files })
                    }}
                >
                    <DocumentFlatTree
                        size={smallMenu ? 'small' : 'medium'}
                        rootId={props.file.documentRootId}
                        referenceNumber={props.file.reference}
                        sortOrder={sortOrder}
                        selectionStrategy={DocumentSelectionStrategy.Single}
                        selectedOptions={props.selectedDriveItemIds}
                        onOptionsSelect={(ids, items) => {
                            props.setSelectedDriveItemIds(ids)
                            props.setSelectedDriveItems(items)
                        }}
                        setIsLoading={setIsLoading}
                        onDriveItemsLoaded={(allItems, filteredItems) => {
                            setAllDriveItems(allItems)
                            setFilteredDriveItems(filteredItems)
                        }}
                        setUpdateDriveItems={setter => (updateDriveItemsFunc.current = setter)}
                        setReloadDriveItems={setter => (reloadDriveItemsFunc.current = setter)}
                        treeItemContent={(flatTree, props, flatTreeItem, isContentLoading, fileCount) => (
                            <SimpleDocumentFlatTreeItem
                                key={flatTreeItem.value}
                                {...flatTreeItem.getTreeItemProps()}
                                {...props}
                                item={flatTreeItem.getTreeItemProps()}
                                flatTreeHook={flatTree}
                                itemType={flatTreeItem.itemType}
                                size={props.size}
                                isContentLoading={isContentLoading}
                                fileCount={fileCount}
                                onPreviewClick={driveItem => setOpenPreviewDocument(driveItem)}
                                onRenameClick={driveItem => setOpenMutateDocuments({ action: MutateDocumentAction.Rename, items: [driveItem] })}
                            />
                        )}
                    />
                </Dropzone>
            </Card>
        </>
    )
}
